import Skeleton from '@/common/components/skeleton/Skeleton';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { resolveProfileCity } from '@/pages/customer/home/components/profileDetailsModal/getProfileAttributes';
import ChatLevelProgress from './ChatLevelProgress';
import {
	ActiveCallPill,
	CallButton,
	CallButtonIcon,
	CallButtonLabel,
	CallWave,
	CallWaveBar,
	CallTime,
	ChatBackButton,
	HangupButton,
	ProfileActions,
	ProfileAvatar,
	ProfileAvatarWrap,
	ProfileCallDot,
	ProfileIdentity,
	ProfileMeta,
	ProfileName,
	ProfileOrigin,
	ProfilePanelToggle,
	ProfilePanelToggleArrow,
	SelectedProfileRoot,
} from './chatSelectedProfile.styles';

const PhoneIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true'>
		<path d='M22 16.9v2.6a2 2 0 0 1-2.18 2 19.7 19.7 0 0 1-8.59-3.05 19.4 19.4 0 0 1-6-6A19.7 19.7 0 0 1 2.18 3.8 2 2 0 0 1 4.17 2h2.6a2 2 0 0 1 2 1.72c.12.9.32 1.78.6 2.62a2 2 0 0 1-.45 2.11L7.8 9.57a16 16 0 0 0 6.63 6.63l1.12-1.12a2 2 0 0 1 2.11-.45c.84.28 1.72.48 2.62.6A2 2 0 0 1 22 16.9Z' />
	</svg>
);

const HangupIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true'>
		<path d='M5.6 15.7c4.1-3.2 8.7-3.2 12.8 0l1.4-2.1c.5-.8.3-1.8-.5-2.3-4.6-2.9-10.8-2.9-15.4 0-.8.5-1 1.5-.5 2.3l1.4 2.1c.2.3.6.3.8 0Z' />
		<path d='m8.1 14.2-.8 3.1M15.9 14.2l.8 3.1' />
	</svg>
);

const BackIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="M19 12H5" />
		<path d="M12 19l-7-7 7-7" />
	</svg>
);

const formatCallTime = (seconds) => {
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
};

const ChatSelectedProfile = ({
	person,
	imageSrc,
	isProfilePanelOpen,
	isVoiceCallActive,
	voiceCallSeconds,
	voiceWaveMode = 'silent',
	scoreProgress,
	isScoreProgressLoading = false,
	onCall,
	onEndCall,
	onToggleProfilePanel,
	showBackButton = false,
	onBack,
}) => {
	const t = useLanguageData('chat');
	const profileT = useLanguageData('profileModal');
	const city = resolveProfileCity(person, profileT);

	return (
		<SelectedProfileRoot $showBack={showBackButton}>
			{showBackButton && (
				<ChatBackButton
					type="button"
					onClick={onBack}
					onTouchEnd={(event) => {
						event.preventDefault();
						onBack?.(event);
					}}
					aria-label={t.back}
				>
					<BackIcon />
				</ChatBackButton>
			)}
			<ProfileAvatarWrap>
				{imageSrc ? (
					<ProfileAvatar src={imageSrc} alt={person?.name} />
				) : (
					<Skeleton
						width='100%'
						height='100%'
						radius='50%'
						minWidth='100%'
						minHeight='100%'
					/>
				)}
				{isVoiceCallActive && (
					<ProfileCallDot $waveMode={voiceWaveMode} aria-hidden='true' />
				)}
			</ProfileAvatarWrap>
			<ProfileMeta>
				<ProfileIdentity>
					{person?.name ? (
						<ProfileName>{person.name}</ProfileName>
					) : (
						<Skeleton width="120px" height="18px" radius="6px" />
					)}
					{city && <ProfileOrigin>{city}</ProfileOrigin>}
				</ProfileIdentity>
			</ProfileMeta>
			<ProfileActions>
				<ChatLevelProgress
					scoreProgress={scoreProgress}
					isLoading={isScoreProgressLoading}
					conversationKey={person?._id || null}
				/>
				{isVoiceCallActive ? (
					<ActiveCallPill
						role='status'
						aria-label={t.callInProgress}
						$waveMode={voiceWaveMode}
					>
						<CallWave aria-hidden='true'>
							<CallWaveBar
								$waveMode={voiceWaveMode}
								$active={voiceWaveMode !== 'silent'}
							/>
							<CallWaveBar
								$waveMode={voiceWaveMode}
								$active={voiceWaveMode !== 'silent'}
							/>
							<CallWaveBar
								$waveMode={voiceWaveMode}
								$active={voiceWaveMode !== 'silent'}
							/>
							<CallWaveBar
								$waveMode={voiceWaveMode}
								$active={voiceWaveMode !== 'silent'}
							/>
							<CallWaveBar
								$waveMode={voiceWaveMode}
								$active={voiceWaveMode !== 'silent'}
							/>
						</CallWave>
						<CallTime>{formatCallTime(voiceCallSeconds)}</CallTime>
						<HangupButton
							type='button'
							onClick={onEndCall}
							aria-label={t.endCall}
						>
							<HangupIcon />
						</HangupButton>
					</ActiveCallPill>
				) : (
					<CallButton type='button' onClick={onCall} aria-label={t.call}>
						<CallButtonIcon aria-hidden='true'>
							<PhoneIcon />
						</CallButtonIcon>
						<CallButtonLabel>{t.call}</CallButtonLabel>
					</CallButton>
				)}
				<ProfilePanelToggle
					type='button'
					$panelOpen={isProfilePanelOpen}
					onClick={onToggleProfilePanel}
					aria-label={
						isProfilePanelOpen ? t.hideProfilePanel : t.showProfilePanel
					}
					aria-expanded={isProfilePanelOpen}
				>
					<ProfilePanelToggleArrow
						$panelOpen={isProfilePanelOpen}
						aria-hidden='true'
					/>
				</ProfilePanelToggle>
			</ProfileActions>
		</SelectedProfileRoot>
	);
};

export default ChatSelectedProfile;
